import IconRemoveCircle from "@shared/components/icons/RemoveCircle"
import IconSparkle from "@shared/components/icons/Sparkle"
import { useDocumentStore } from "@shared/components/providers/SupergrainProvider"
import { cx } from "@shared/components/untitledui/util/cx"
import type { CardStack, CardStackSummary, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course, CourseCalendarTime } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { reportMenuOpenChange } from "@shared/unified-tiptap/analytics/open-menu-event"
import { LessonTitleChromeProvider } from "@shared/unified-tiptap/lesson-title-chrome-context"
import { tracked } from "@supergrain/kernel/react"
import type { Editor } from "@tiptap/core"
import { prepareMoveLessonToDate } from "cc-frontend/lib/actions/course/calendar/MOVE_LESSON_TO_DATE"
import { prepareMoveLessonToDateOnDifferentCourse } from "cc-frontend/lib/actions/course/calendar/MOVE_LESSON_TO_DATE_ON_DIFFERENT_COURSE"
import { prepareCourseAssignLessonTemplateToWeek } from "cc-frontend/lib/actions/course/COURSE_ASSIGN_LESSON_TEMPLATE_TO_WEEK"
import COURSE_DATE_ANNOTATION_HIDE from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_HIDE"
import lessonViewHistoryAction from "cc-frontend/lib/actions/lesson/LESSON_VIEW_HISTORY"
import { devLessonEditorSwitch } from "cc-frontend/lib/dev-lesson-editor-switch"
import type CourseCalendarEditor from "cc-frontend/services/course-calendar-editor"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { LessonModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Notifier from "cc-frontend/services/notifier"
import type PatchCoordinator from "cc-frontend/services/patch-coordinator"
import type PlanbookEditor from "cc-frontend/services/planbook-editor"
import type Session from "cc-frontend/services/session"
import {
  extractCardStackTemplateIds,
  isCourseDateAnnotationSingle,
} from "cc-frontend/utils/filter-dates/course"
import moment from "moment"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useCallback, useRef, useState } from "react"
import UnitsInHeader from "react-app/components/planner/card-stacks/lesson/UnitsInHeader"
import { useAsEmberDocument } from "react-app/hooks/useAsEmberDocument"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useOpenStandardsModal } from "react-app/hooks/useOpenStandardsModal"
import { useEmberService } from "react-app/providers/ember-service-context"
import { mergeProps, useButton } from "react-aria"
import { Button as AriaButton } from "react-aria-components"
import { UnifiedGoogleClassroomCardEditor } from "../unified-google-classroom-card-editor"
import UnifiedCardStackEditor from "../UnifiedCardStackEditor"
import AddToDayButton from "./AddToDayButton"
import BlankNativeDragImage from "./BlankNativeDragImage"
import ClassOffMenu, { type OffCause } from "./ClassOffMenu"
import CourseTitleBar from "./CourseTitleBar"
import DanglingLessonNotice from "./DanglingLessonNotice"
import { dayNoteAnnotation, dayOffAnnotation } from "./day-off-annotation"
import ExpandCollapseButton from "./ExpandCollapseButton"
import styles from "./InstructionalLesson.module.css"
import {
  session as dragSession,
  type LessonDragSource,
  type LessonDropTarget,
} from "./lesson-drag-session"
import { studentLessonUrl } from "./lesson-links"
import type { RemoveClassTodayChoice } from "./lesson-menu-views/remove-class-today-flow"
import LessonAi from "./LessonAi"
import LessonComments from "./LessonComments"
import LessonDateAnnotations from "./LessonDateAnnotations"
import LessonDragGrip from "./LessonDragGrip"
import LessonDropNotice from "./LessonDropNotice"
import LessonMenu from "./LessonMenu"
import { useFocusedLessonScroll } from "./use-focused-lesson-scroll"
import { useLessonClipboard } from "./use-lesson-clipboard"
import { useLessonDragSource, useLessonDropTarget, type CommitLessonMove } from "./use-lesson-drag"
import { useLessonDragGhost } from "./use-lesson-drag-ghost"
import { useLessonTitleFloor } from "./use-lesson-title-floor"

interface Props {
  courseDate: CourseDateCombined
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  units: Array<CardStackSummary> | null
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  modelsNeededForFirstEdit: LessonModelsNeededForFirstEdit
  editCourseTitleAndOrder: (courseId: string) => void
  /** The planner's collapse-all: every lesson shows its title only. */
  lessonsCollapsed: boolean
  isFullScreen?: boolean
  /** No drag pad, and no drag: a lesson shown out of its calendar has nowhere to move to. */
  dragDisabled?: boolean
}

function NewLessonIndicator() {
  return (
    <span
      className="flex h-6 items-center rounded-full border border-grey px-1.5 text-[0.625rem] text-grey select-none"
      data-test="new lesson indicator"
    >
      NEW
    </span>
  )
}

/**
 * Opening springs rather than running a duration out — a symmetric ease over a fixed 0.3s read
 * as slow at this size. It arrives in about 230ms and settles back through roughly 2% of its
 * own height. Bounce is deliberately that low: the whole day's column slides with this panel,
 * and a larger overshoot stops reading as a settle and starts reading as the lesson wobbling.
 */
const AI_OPEN_TRANSITION = { type: "spring", visualDuration: 0.25, bounce: 0.22 } as const

/** Closing is a plain ease-in: a bounce on the way out looks like the panel missed the latch. */
const AI_CLOSE_TRANSITION = { duration: 0.18, ease: "easeIn" } as const

const NO_MOTION_TRANSITION = { duration: 0 } as const

function AiButton({ open }: { open: () => void }) {
  return (
    <AriaButton
      onPress={open}
      data-test="ai button"
      className="flex h-6 cursor-pointer items-center rounded-full bg-white px-2 text-[0.84375rem] text-[orange] shadow-sm outline-hidden"
    >
      <span className="mr-0.75 flex items-center justify-center">
        <IconSparkle className="h-[0.9rem] w-[0.9rem]" />
      </span>
      AI
    </AriaButton>
  )
}

/** Stands in for the AI button while the panel it opened is up. */
function CloseAiButton({ close }: { close: () => void }) {
  return (
    <AriaButton
      onPress={close}
      aria-label="Close AI"
      data-test="close ai"
      className="flex size-6 cursor-pointer rounded-full text-black/40 outline-hidden hover:text-black/60"
    >
      <IconRemoveCircle className="size-6 fill-current" />
    </AriaButton>
  )
}

/**
 * A lesson slot: course title bar, menus, annotations, the editor body, and the drag/drop mechanics
 * (grip, ghost, drop notice, collapse). It is also the single place the lesson slot touches
 * Ember services and the dispatcher — the pieces it renders take data and the callbacks
 * defined here, oblivious to what they tie into.
 */
const InstructionalLesson = tracked(function ({
  courseDate,
  lesson,
  units,
  course,
  planbook,
  rotationCalendar,
  modelsNeededForFirstEdit,
  editCourseTitleAndOrder,
  lessonsCollapsed,
  isFullScreen,
  dragDisabled,
}: Props) {
  /* -------------------------------------------------------------------------- */
  /* Ember services */
  /* -------------------------------------------------------------------------- */

  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const courseCalendarEditor = useEmberService<CourseCalendarEditor>("course-calendar-editor")
  const planbookEditor = useEmberService<PlanbookEditor>("planbook-editor")
  const modalManager = useEmberService<ModalManager>("modal-manager")
  const notifier = useEmberService<Notifier>("notifier")
  const asEmberDocument = useAsEmberDocument()
  const session = useEmberService<Session>("session")
  const dispatcher = useEmberService<Dispatcher>("dispatcher")
  const patchCoordinator = useEmberService<PatchCoordinator>("patch-coordinator")
  const dispatch = useDispatch()
  const store = useDocumentStore()
  const {
    canPaste: canShowPasteLesson,
    copyLesson,
    pasteLesson,
    pasteInto,
  } = useLessonClipboard({ lesson, course, planbook, modelsNeededForFirstEdit })

  /* -------------------------------------------------------------------------- */
  /* This day's lesson */
  /* -------------------------------------------------------------------------- */

  const editorRef = useRef<Editor | null>(null)
  const dateString = courseDate.dateString
  const canEditLesson = lesson.meta?.permissions?.includes("can-edit-plan") ?? false
  // A real lesson, or the day's template standing in for one until the user edits it.
  const hasCardStack = !!courseDate.cardStackId || !!courseDate.cardStackTemplateId
  // A template lesson (cardStackTemplateId, no cardStackId yet) still counts as draggable —
  // the drop materializes it — but an empty day has nothing to drag.
  const canDrag = canEditLesson && hasCardStack && !dragDisabled
  // Opened back up against the planner's collapse-all — per lesson, like the legacy planner.
  const [isExpanded, setIsExpanded] = useState(false)
  const [isAiOpen, setIsAiOpen] = useState(false)
  // The AI panel's own exit handler: the close button sits up here in the header, but closing
  // has to run past the panel first (it confirms when the assistant has edits waiting).
  const requestCloseAi = useRef<(() => void) | null>(null)
  const hasAiFeature = session.user?.content?.attributes.featureFlags.AI ?? false
  const reduceMotion = useReducedMotion()

  /* -------------------------------------------------------------------------- */
  /* Actions passed to the menus and the editor */
  /* -------------------------------------------------------------------------- */

  const openStandardsModal = useOpenStandardsModal(planbook, course)

  /**
   * The lesson menu's open edge, reported through this lesson's editor: the menu sits outside the
   * editor, but it acts on the lesson the editor is editing, and the analytics runtime — host
   * identity and port — lives on that editor. It targets no card, and it is opens-only: the
   * menu's rows run their own actions, which report themselves.
   */
  const reportLessonMenuOpenChange = (isOpen: boolean) => {
    const editor = editorRef.current
    if (!editor) return
    reportMenuOpenChange(editor, "lesson-menu", isOpen, () => null)
  }

  // Google Classroom cards are edited in the Google Classroom sidebar (the same modal
  // the legacy planner opens — see planner/card-stacks/cards.ts). The sidebar's edits go
  // through a UnifiedGoogleClassroomCardEditor, which updates the cardGoogleClassroom
  // node in this lesson's editor. Since that happens when the editor is not focused,
  // UnifiedCardStackEditor commits each edit immediately as a LESSON_UNIFIED_UPDATE action.
  // TODO: Change to dedicated GC action(s), at least in the narrative + newAnalyticsEvents.
  const openGoogleClassroomSidebar = useCallback(
    (cardId: string) => {
      if (!canEditLesson) return
      const editor = editorRef.current
      if (!editor) return
      ;(document.activeElement as HTMLElement | null)?.blur()

      const cardEditor = new UnifiedGoogleClassroomCardEditor({
        editor,
        cardId,
        onEdited: () => {},
      })
      modalManager.openModal(
        "planner/modals/google-classroom",
        {
          cardEditor,
          dateString: courseDate.dateString,
          cardStackId: lesson.id,
          planbook: asEmberDocument(planbook),
        },
        { onClose: () => cardEditor.dispose() },
      )
    },
    [canEditLesson, courseDate.dateString, lesson.id, planbook],
  )

  // The menu calls this and closes, so nothing downstream is left to await the read. A template
  // that cannot be read has to say so here or the press does nothing at all.
  const pasteTemplate = (templateId: string) => {
    void pasteTemplateOrWarn(templateId).catch(() =>
      notifier.warning("We couldn't open that template. Please try again."),
    )
  }

  const pasteTemplateOrWarn = async (templateId: string) => {
    // Prefer the handle's current value: the find promise memoizes its first resolution.
    const templateHandle = store.find("card-stack", templateId)
    const template = templateHandle.value ?? (await templateHandle.promise!)
    pasteInto(template.attributes, "template", {
      id: template.id,
      // The template's own planbook, which for a shared template is the group's — not this
      // planbook, which never held it.
      planbookId: template.relationships.planbook.data.id,
      courseId: null,
      ownerIds: [planbook.relationships.owner.data.id],
      version: template.attributes.version,
      source: "template",
    })
  }

  // Not on a day the class isn't meeting, where the lesson would have nowhere to sit, nor while the
  // AI panel is open: accepting its edits writes the editor's content over the copy.
  const copyFollowedLesson =
    canEditLesson && !courseDate.isOff && !isAiOpen
      ? (followedLesson: UnifiedCardStack, followedDateString: string) =>
          lessonEditor.copyFollowedLesson(
            modelsNeededForFirstEdit,
            lesson,
            course,
            planbook,
            followedLesson,
            followedDateString,
          )
      : undefined

  const copyFromAnotherClass = () => {
    modalManager.openModal("planner/modals/import-lessons", {
      dateString,
      course: asEmberDocument(course),
    })
  }

  const saveAsTemplate = (name: string) =>
    planbookEditor.saveAsLessonTemplate(course, planbook, lesson.attributes, name)

  const setCustomTime = (time: CourseCalendarTime | null) =>
    lessonEditor.setCustomTime(modelsNeededForFirstEdit, course, dateString, planbook.id, time)

  const bumpLessonForward = () => {
    courseCalendarEditor.bumpLesson(course, dateString, planbook, rotationCalendar)
  }

  const pullLessonBackwards = () =>
    courseCalendarEditor.pullLessonBackwards(dateString, course, planbook, rotationCalendar)

  // Erased with its version history, so a bumped lesson can land in the slot it leaves behind.
  const eraseLesson = () => {
    if (!courseDate.cardStackId) return
    lessonEditor.eraseLesson(course, dateString, courseDate.cardStackId, courseDate.timeCustom)
  }

  // The Ember no-class flow: turn the day off with an optional note, bumping or deleting
  // the lesson as the user chose.
  const removeClassToday = ({ bumpOrDelete, note }: RemoveClassTodayChoice) => {
    courseCalendarEditor.turnOffAndBumpDeleteOrNothing({
      // Two annotations, not one: the off marker turns the day off, and the note — if the user
      // left one — is pinned to the day on its own, so turning the class back on keeps it.
      dateAnnotation: dayOffAnnotation({ dateString }),
      noteAnnotation: note === null ? null : dayNoteAnnotation({ dateString, note }),
      course,
      planbook,
      cardStackId: courseDate.cardStackId,
      rotationCalendar,
      dateString,
      bumpOrDelete: bumpOrDelete ?? "NO_LESSON",
    })
  }

  /**
   * Why the class isn't meeting, which decides how — and whether — it can be turned back on.
   * `isOff` is `planbookIsOff || (courseIsOff && !isForcedOn)`, so a day the course did not turn
   * off itself is the planbook's doing, and no press on this class can cancel that.
   */
  const offCause: OffCause = course.attributes.calendar.datesOff.includes(dateString)
    ? "note"
    : courseDate.isOffByDefault
      ? "schedule"
      : "planbook"

  const turnClassBackOn = () =>
    courseCalendarEditor.turnDateOn({ course, dateString, calledFrom: "lesson-menu" })

  // Every note on the day, not just one: each is its own annotation, and one left showing keeps
  // the class on the calendar. The calc only puts single-date annotations here; this narrows it.
  const hideClassToday = () => {
    for (const annotation of courseDate.visibleAnnotations.filter(isCourseDateAnnotationSingle)) {
      dispatcher.dispatchAction(COURSE_DATE_ANNOTATION_HIDE, { annotation, course })
    }
  }

  const emailLesson = () => {
    modalManager.openModal("planner/menus/parts/email-link-form", {
      pathname: `/lessons/${lesson.id}`,
      baseUrlParams: new URLSearchParams(window.location.search),
      // A lesson link never shows the planbook's navigation.
      hideNav: true,
      dateString,
      courseTitle: course.attributes.title,
      planbook: asEmberDocument(planbook),
      // Already an Ember document — session.user is a findDocument proxy over the Ember store.
      user: session.user.content,
    })
  }

  const printOrDownload = () => {
    modalManager.openModal("planner/modals/print-download", {
      courseId: course.id,
      planbookId: planbook.id,
      startDate: dateString,
      endDate: dateString,
      layout: "course-date:single",
      currentUserId: session.userId,
      canPrint: true,
      dateRangeString: moment(dateString).format("dddd, MMMM Do YYYY"),
    })
  }

  // Google Classroom's share page takes a link and a title; the link is the class website's, the
  // only version of the lesson a student can open.
  const shareToGoogleClassroom = () => {
    const url = encodeURIComponent(studentLessonUrl(course.id, dateString))
    const title = encodeURIComponent(lesson.attributes.title ?? "")
    window.open(
      `https://classroom.google.com/share?url=${url}&title=${title}`,
      "_blank",
      "noopener",
    )
  }

  const browseLessonHistory = () => {
    modalManager.openModal("planner/modals/lesson-history", { cardStack: asEmberDocument(lesson) })
    dispatch(
      lessonViewHistoryAction({
        cardStackId: lesson.id,
        planbookId: planbook.id,
        courseId: course.id,
      }),
    )
  }

  const openFullScreen = () => {
    modalManager.openModal("planner/modals/full-screen", {
      course: asEmberDocument(course),
      planbook: asEmberDocument(planbook),
      rotationCalendar: asEmberDocument(rotationCalendar),
      editCourseTitleAndOrder,
      dateString: courseDate.dateString,
      classNames: "take-course-background-color",
    })
  }

  const openTemplateGallery = (afterChooseTemplate?: () => void) => {
    modalManager.openModal("planner/modals/template-library", {
      cardStack: asEmberDocument(lesson),
      course: asEmberDocument(course),
      planbook: asEmberDocument(planbook),
      rotationCalendar: asEmberDocument(rotationCalendar),
      // These are just for actions, not Ember rendering -> no need to convert.
      modelsNeededForFirstEdit,
      afterChooseTemplate,
    })
  }

  // What the class starts its unplanned days from, when that is one answer. Assigning a default
  // writes the same template to every default day, so a class whose days were set individually
  // in planbook settings has several — and no single one of them to check in the chooser.
  const defaultTemplateIds = extractCardStackTemplateIds(course)
  const defaultTemplateId = defaultTemplateIds.length === 1 ? defaultTemplateIds[0]! : null

  // Where the day's template comes from in the first place: the template on the class's default
  // days, which is what an unplanned day resolves to. Assigning it rewrites no lesson — a day
  // that already holds one has its own `cardStackId` and never reads this.
  const setDefaultTemplate = (templateId: string) => {
    dispatcher.dispatchPreparedAction(
      prepareCourseAssignLessonTemplateToWeek({
        course,
        cardStackId: templateId,
        planbookId: planbook.id,
        rotationCalendar,
      }),
    )
  }

  // The same gallery the lesson uses, told to set the default instead of pasting into today.
  const openDefaultTemplateGallery = () => {
    modalManager.openModal("planner/modals/template-library", {
      cardStack: asEmberDocument(lesson),
      course: asEmberDocument(course),
      planbook: asEmberDocument(planbook),
      rotationCalendar: asEmberDocument(rotationCalendar),
      modelsNeededForFirstEdit,
      chooseTemplate: (template: CardStack) => setDefaultTemplate(template.id),
    })
  }

  /* -------------------------------------------------------------------------- */
  /* Dragging and Dropping */
  /* -------------------------------------------------------------------------- */

  const dragSource: LessonDragSource = {
    lesson,
    course,
    planbook,
    rotationCalendar,
    dateString: courseDate.dateString,
    modelsNeededForFirstEdit,
    getEditor: () => editorRef.current,
  }
  const dropTarget: LessonDropTarget = { course, planbook, rotationCalendar, courseDate }

  // The persistence side of a drop: materialize a phantom/template source lesson (idempotent),
  // then dispatch the real move — same-course or cross-course. useLessonDropTarget has already
  // confirmed acceptance and resolved `source`.
  //
  // Deferred one frame: materializing the source's replacement lesson changes its id, which
  // remounts UnifiedCardStackEditor (keyed on lesson.id) and tears the native drag grip out of
  // the DOM. Do that on the drop's own tick and the browser has no source node left to dispatch
  // its native `dragend` at, so react-aria's onDragEnd — and the ghost/session teardown riding on
  // it — never fires, leaving the drag ghost and grabbing cursor stuck. One rAF lets dragend fire
  // first.
  const onCommitMove: CommitLessonMove = (source, target) => {
    requestAnimationFrame(() => {
      lessonEditor.createBlankLesson(source.modelsNeededForFirstEdit)
      if (source.course.id === target.course.id) {
        dispatch(
          prepareMoveLessonToDate({
            cardStackId: source.lesson.id,
            fromDate: source.dateString,
            toDate: target.courseDate.dateString,
            course: source.course,
            planbook: source.planbook,
            rotationCalendar: source.rotationCalendar,
          }),
        )
      } else {
        dispatch(
          prepareMoveLessonToDateOnDifferentCourse({
            cardStackId: source.lesson.id,
            fromDate: source.dateString,
            toDate: target.courseDate.dateString,
            fromCourse: source.course,
            toCourse: target.course,
            planbook: source.planbook,
            rotationCalendar: source.rotationCalendar,
          }),
        )
      }
    })
  }

  // A ref on the course title bar so the ghost can size itself to the exact grabbed header.
  const courseTitleBarRef = useRef<HTMLDivElement>(null)
  // The visible drag ghost: a React-portal follower card driven by react-aria's drag
  // stream (onDragMove/onDragEnd below); react-aria's <DragPreview> blanks the native image.
  const dragGhost = useLessonDragGhost(
    courseTitleBarRef,
    <>
      <div className="leading-snug font-bold [overflow-wrap:anywhere] text-gray-900">
        {lesson.attributes.title || "Untitled lesson"}
      </div>
      <div className="mt-0.5 text-xs leading-tight font-medium [overflow-wrap:anywhere] text-gray-500">
        {course.attributes.title}
      </div>
    </>,
  )
  const { dragProps, dragButtonProps, isDragging, previewRef } = useLessonDragSource(
    dragSource,
    canDrag,
    { onDragEnd: dragGhost.onDragEnd },
  )
  const gripRef = useRef<HTMLDivElement>(null)
  const { buttonProps: gripButtonProps } = useButton(
    { ...dragButtonProps, elementType: "div" },
    gripRef,
  )
  const dropRef = useRef<HTMLDivElement>(null)
  const { dropProps, isDropTarget } = useLessonDropTarget(dropTarget, dropRef, onCommitMove)
  useFocusedLessonScroll(dropRef, course.id)

  /* -------------------------------------------------------------------------- */
  /* Booleans we use to show/hide different parts of the template */
  /* -------------------------------------------------------------------------- */

  const courseColor = course.attributes.color

  // Whether a drag is in flight — of ANY lesson, not just this one — read reactively off the
  // global drag session, because every slot on screen reacts to it.
  const isAnyLessonDragging = dragSession().kind === "dragging"

  // The white body under the course bar is one of four things:
  //   this day's lesson in its editor         — the day has a lesson
  //   that lesson collapsed to its title      — ...and a drag is in flight
  //   an empty, comfortably sized landing pad — the day has no lesson, and a drag is in flight
  //   nothing at all, just the course bar     — the day has no lesson and no drag is in flight
  // Show the title only: while a lesson is mid-drag (so a week of drop targets fits on screen),
  // or while the planner's collapse-all is on and this lesson hasn't been opened back up. The
  // body keeps its title — and the drag grip pinned beside it — by collapsing to a measured
  // floor rather than to zero.
  const showsTitleOnly = isAnyLessonDragging || (lessonsCollapsed && !isExpanded)
  const bodyRef = useLessonTitleFloor()

  // Every day with no lesson of its own that isn't filling itself from a template: the class
  // opted out of automatic templates, or it has no template to apply. One shape for both — a day
  // waiting to be asked for looks the same either way. Purely a view choice: the day's template
  // still resolves and its phantom still carries it, so opting back in shows the same lesson.
  const showsAddLessonRow =
    courseDate.cardStackId === null &&
    (course.attributes.doNotApplyTemplatesAutomatically === true ||
      courseDate.cardStackTemplateId === null)
  // Nothing paints for a reader: the row is an invitation to add, and they cannot. Nor on a day
  // the class isn't meeting — a lesson added to it would have nowhere to sit.
  const showsAddToDay = showsAddLessonRow && canEditLesson && !courseDate.isOff
  // A lesson on a day that has since been turned off: it still has to be reachable, with a
  // notice saying why it is here (`showLesson` is off-day-aware, unlike `isOff` alone).
  const showsLesson = hasCardStack && courseDate.showLesson && !showsAddLessonRow
  // Every day that could accept the dragged lesson reserves the same landing area — days holding
  // a lesson (whose editor collapses away) and empty days alike, so no drop target is a bare
  // strip and the "drop here" notice always has room. Off days never accept, so they reserve none.
  const reservesLandingPad = isAnyLessonDragging && !courseDate.isOff
  const showsBody = showsLesson || reservesLandingPad
  // A lesson nobody has touched yet reads as provisional: the whole card, course bar included,
  // sits at 80% until you hover it.
  const showsAsNewLesson = showsLesson && courseDate.isNewLesson
  // The units describe the day, not the lesson. Show them on every day the class meets.
  const showsUnits = !courseDate.isOff && units !== null && units.length > 0

  /* -------------------------------------------------------------------------- */
  /* The header's menus, and the add-lesson row */
  /* -------------------------------------------------------------------------- */

  // While the AI panel is open, this button closes it. The Lesson Menu beside it does not change.
  const aiButton = () => {
    // A day still waiting to be asked for a lesson gets the button too. Opening the panel stands
    // the day's lesson up first, and the phantom it stands up already carries the day's template
    // — the same lesson the Add Lesson row's top choice applies.
    if ((!showsLesson && !showsAddToDay) || !hasAiFeature || !canEditLesson || showsTitleOnly) {
      return null
    } else if (isAiOpen) {
      return <CloseAiButton close={() => requestCloseAi.current?.()} />
    } else {
      const doOpen = () => {
        lessonEditor.createBlankLesson(modelsNeededForFirstEdit)
        setIsAiOpen(true)
      }

      return (
        <AiButton
          open={() => {
            if (courseDate.cardStackId === null && courseDate.cardStackTemplateId === null) {
              // The user needs to choose a template first, so that we can
              // instantiate it as a blank lesson.
              openTemplateGallery(doOpen)
              return
            }
            doOpen()
          }}
        />
      )
    }
  }

  // Every day the class meets has the Lesson Menu, also a day with no lesson. That day needs it:
  // class settings, paste, and turn the class off are only in this menu.
  const headerMenu = () => {
    if (courseDate.isOff) {
      return (
        <ClassOffMenu
          course={course}
          courseDate={courseDate}
          offCause={offCause}
          canEdit={canEditLesson}
          turnClassBackOn={turnClassBackOn}
          hideClassToday={hideClassToday}
          editCourseTitleAndOrder={editCourseTitleAndOrder}
          bumpLessonForward={bumpLessonForward}
          copyLesson={copyLesson}
          eraseLesson={eraseLesson}
        />
      )
    } else {
      return (
        <>
          {aiButton()}
          <LessonMenu
            lesson={lesson}
            course={course}
            planbook={planbook}
            courseDate={courseDate}
            editCourseTitleAndOrder={editCourseTitleAndOrder}
            pasteLesson={pasteLesson}
            canPasteLesson={canShowPasteLesson}
            isFullScreen={isFullScreen}
            copyLesson={copyLesson}
            copyFromAnotherClass={copyFromAnotherClass}
            bumpLessonForward={bumpLessonForward}
            pullLessonBackwards={pullLessonBackwards}
            dayHasTinyUnit={courseCalendarEditor.doesCourseDateHaveTinyUnit(course, dateString)}
            eraseLesson={eraseLesson}
            removeClassToday={removeClassToday}
            emailLesson={emailLesson}
            printOrDownload={printOrDownload}
            shareToGoogleClassroom={shareToGoogleClassroom}
            browseLessonHistory={browseLessonHistory}
            openFullScreen={openFullScreen}
            openTemplateGallery={openTemplateGallery}
            pasteTemplate={pasteTemplate}
            saveAsTemplate={saveAsTemplate}
            setDefaultTemplate={setDefaultTemplate}
            openDefaultTemplateGallery={openDefaultTemplateGallery}
            defaultTemplateId={defaultTemplateId}
            onOpenChange={reportLessonMenuOpenChange}
            switchToLegacyEditor={() =>
              devLessonEditorSwitch.toggle(patchCoordinator, lesson, course.id, dateString)
            }
          />
        </>
      )
    }
  }

  // One press, one outcome: the day gets a lesson of its own, still showing the day's template
  // because its cards are null — a phantom made real, not a copy of the template. Everything else
  // a day could be started from — pasting, another class's lesson, some other template — is in the
  // lesson menu beside it. With no template there is nothing to inherit, so the press picks one.
  const addLessonRow = () => {
    if (!showsAddToDay) return null
    const hasTemplate = courseDate.cardStackTemplateId !== null
    return (
      <AddToDayButton
        label="Add Lesson"
        dataTest="add lesson"
        onPress={
          hasTemplate
            ? () => lessonEditor.persistPhantomLesson(modelsNeededForFirstEdit)
            : () => openTemplateGallery()
        }
        // White, like the lesson body it stands in for — an instructional day's card is course-
        // coloured down to the bar and white below it, whether or not the lesson exists yet.
        className="bg-white"
      />
    )
  }

  return (
    <>
      <div
        ref={dropRef}
        // Programmatically focusable (not a tab stop) so react-aria's keyboard drag can move
        // focus onto it as the current drop target.
        tabIndex={-1}
        data-test="lesson slot"
        // Slot identity for tests, which address a slot by course + date (the planner's
        // re-renders replace this DOM wholesale, so an element handle can't be held).
        data-course-id={course.id}
        data-date-string={courseDate.dateString}
        // set-course-color-vars + data-color publish --course-color so the drop-target glow and
        // the notice tint (LessonDropNotice.module.css) are the course's own color.
        data-color={courseColor}
        // Dim the source while it is being dragged; give the current drop target a soft
        // course-colored glow + gentle lift. mb-3 is the space between classes; relative
        // anchors the notice overlay.
        className={cx(
          "set-course-color-vars take-course-background-color relative mb-3 rounded-md shadow-md",
          styles.slot,
          isDropTarget && styles.slotDropTarget,
          showsAsNewLesson && styles.newLessonSlot,
        )}
        style={{
          borderRadius: "0.375rem",
          ...(isDragging ? { opacity: 0.4 } : null),
        }}
        {...dropProps}
      >
        {isDropTarget && <LessonDropNotice bumpsExistingLesson={courseDate.hasLesson} />}

        {/* The course title bar is always here — it is what says which class this slot is. Under
            it goes the note, or the lesson: a class that isn't meeting shows the note instead of
            a lesson, never both. */}
        <CourseTitleBar
          ref={courseTitleBarRef}
          course={course}
          courseDate={courseDate}
          planbook={planbook}
          rotationCalendar={rotationCalendar}
          setCustomTime={setCustomTime}
          onCopyFollowedLesson={copyFollowedLesson}
          parentCardStack={lesson.attributes.parentCardStack ?? null}
          isCollapsed={showsTitleOnly}
          editCourseTitleAndOrder={editCourseTitleAndOrder}
          hasBodyBelow={
            showsBody || showsAddToDay || showsUnits || courseDate.hasVisibleAnnotations || isAiOpen
          }
          menu={headerMenu()}
        />

        {/* Directly under the bar, above everything the day holds: the panel is what the user is
            talking to, and the answer lands in the lesson below it. Height, so that lesson slides
            with the panel; it is clipped while it moves. */}
        <AnimatePresence initial={false}>
          {isAiOpen && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{
                height: 0,
                transition: reduceMotion ? NO_MOTION_TRANSITION : AI_CLOSE_TRANSITION,
              }}
              transition={reduceMotion ? NO_MOTION_TRANSITION : AI_OPEN_TRANSITION}
              className="overflow-hidden bg-white"
            >
              <LessonAi
                lesson={lesson}
                course={course}
                planbook={planbook}
                modelsNeededForFirstEdit={modelsNeededForFirstEdit}
                dateString={dateString}
                editorRef={editorRef}
                onClose={() => setIsAiOpen(false)}
                requestCloseRef={requestCloseAi}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Above the units, and above the collapser that the lesson body can shrink into: what
            someone said about this lesson is read before the lesson, and collapse-all keeps it. */}
        <LessonComments commentIds={lesson.attributes.commentIds} currentUserId={session.userId} />

        {/* One place, under the course bar, for every day the class meets. flow-root keeps the
            block's bottom margin inside this white box. Without it the margin collapses out and
            the course color shows through. */}
        {showsUnits && (
          <div className="flow-root bg-white px-3 pt-3" data-test="units host">
            <UnitsInHeader
              units={units}
              course={course}
              planbook={planbook}
              rotationCalendar={rotationCalendar}
              dateString={dateString}
              editCourseTitleAndOrder={editCourseTitleAndOrder}
              flush
              className="mb-4.5"
            />
          </div>
        )}

        {addLessonRow()}

        {courseDate.hasVisibleAnnotations && (
          <LessonDateAnnotations course={course} courseDate={courseDate} hasSlotBelow={showsBody} />
        )}

        {showsBody && (
          <div
            data-test="lesson body shell"
            className={cx(
              "group/lessonBody relative rounded-b-md bg-white p-3",
              reservesLandingPad && "min-h-16",
            )}
          >
            {courseDate.danglingLessonType && (
              <DanglingLessonNotice danglingLessonType={courseDate.danglingLessonType} />
            )}

            {showsLesson && (
              <>
                {/* The cards collapse away when a drag begins, or under the planner's
                    collapse-all — smoothly (grid-rows 1fr -> 0fr) rather than snapping, and only
                    down to the lesson title. Kept mounted so they re-expand fast. */}
                <div
                  ref={bodyRef}
                  className={cx(styles.bodyCollapser, showsTitleOnly && styles.collapsed)}
                >
                  {/* overflow-y, never overflow-x: Chromium re-hit-tests the drag origin after
                      dragstart and cancels the drag if the grip in the horizontal gutter got
                      clipped. `visible` pairs with `clip`, unlike `hidden`. */}
                  <div
                    className={cx(
                      "min-h-0",
                      styles.editorBody,
                      showsTitleOnly && "overflow-y-clip",
                    )}
                    data-test="lesson editor body"
                  >
                    {/* Pass these components to the lesson-title-component for it to render. */}
                    <LessonTitleChromeProvider
                      value={{
                        grip: canEditLesson && !dragDisabled && (
                          <LessonDragGrip
                            ref={gripRef}
                            {...mergeProps(gripButtonProps, dragProps, dragGhost.spawnProps)}
                            className="pointer-events-none h-full w-full opacity-0 transition-opacity group-hover/lessonBody:pointer-events-auto group-hover/lessonBody:opacity-100 hover:pointer-events-auto hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                          />
                        ),
                        trailing: showsAsNewLesson ? <NewLessonIndicator /> : null,
                      }}
                    >
                      <UnifiedCardStackEditor
                        key={lesson.id}
                        cardStack={lesson}
                        modelsNeededForFirstEdit={modelsNeededForFirstEdit}
                        isNonInstructional={false}
                        openStandardsModal={openStandardsModal}
                        openGoogleClassroomSidebar={openGoogleClassroomSidebar}
                        studentLessonUrl={studentLessonUrl(course.id, courseDate.dateString)}
                        editorRef={editorRef}
                        isAiOpen={isAiOpen}
                      />
                    </LessonTitleChromeProvider>
                  </div>
                </div>

                {/* Collapse-all is otherwise a one-way door: this opens just this lesson. */}
                {lessonsCollapsed && !isAnyLessonDragging && (
                  <ExpandCollapseButton
                    isExpanded={isExpanded}
                    toggle={() => setIsExpanded(!isExpanded)}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>

      {dragGhost.element}
      <BlankNativeDragImage ref={previewRef} />
    </>
  )
})

export default InstructionalLesson
